import React, {
    ChangeEvent,
    SetStateAction,
    useEffect,
    useState,
    KeyboardEvent,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { frameFillBackground } from "components/common/backgroundFrame";

import TitleHeader from "../../../common/TitleHeader";
import { Answer } from "./GroupedEvaluation";

type Props = {
    id: number;
    index: number;
    useDecimal: boolean;
    minValue: number;
    maxValue: number;
    answers: Answer[];
    setAnswers: React.Dispatch<SetStateAction<Answer[]>>;
    reset: boolean;
    subTitle: string;
    isLocked: boolean;
};

const InputEvaluation = ({
    id,
    index,
    useDecimal,
    minValue,
    maxValue,
    answers,
    setAnswers,
    reset,
    subTitle,
    isLocked,
}: Props) => {
    const [inputValue, setInputValue] = useState<string>("");
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();

    useEffect(() => {
        if (reset) {
            setInputValue("");
            setError(null);
        }
    }, [reset]);

    const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { value } = e.currentTarget;

        setInputValue(value);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (["e", "E", "+", "-"].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleBlur = () => {
        const num = Number(inputValue);
        let finalValue: number | null = null;

        if (!isNaN(num)) {
            if (num < minValue) finalValue = minValue;
            else if (num > maxValue) finalValue = maxValue;
            else finalValue = num;
        }

        setInputValue(finalValue !== null ? finalValue.toString() : "");

        const newAnswers = [...answers];
        newAnswers[index] = {
            answer: finalValue,
            weight: 1,
            questionId: id.toString(),
        };

        setAnswers(newAnswers);
    };
    return (
        <Wrapper>
            <TitleHeader title={subTitle} />
            <StyledInput
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                type="number"
                placeholder={t("enterNumber")}
                min={minValue ? minValue : undefined}
                max={maxValue ? maxValue : undefined}
                step={useDecimal ? 0.1 : undefined}
                aria-invalid={!!error}
                disabled={isLocked}
            />
            {error && <ErrorText>{error}</ErrorText>}
        </Wrapper>
    );
};

const Wrapper = styled.div`
    text-align: center;
    margin: 1.5rem 0;
`;

const StyledInput = styled.input`
    margin: auto;
    height: 35px;
    padding: 6px 12px;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 15px;
    width: 100%;
    box-sizing: border-box;
    ${frameFillBackground}

    &[disabled] {
        background: #e8e8e8;
        opacity: 0.7;
        cursor: not-allowed;
    }

    &:focus-visible {
        outline: solid 1px #010203;
    }

    &[aria-invalid="true"] {
        border-color: red;
    }
`;

const ErrorText = styled.p`
    color: red;
    font-size: 0.9rem;
    margin-top: 0.3rem;
`;

export default InputEvaluation;
